import dayjs from "dayjs";

import { Card } from "@components/cards/card";
import { Pagination } from "@components/pagination";
import { LoadingSkeleton } from "@components/skeleton-loading";
import { CustomerConvertStatus } from "@hooks/functions/Customer";
import { Masks } from "@hooks/functions/Masks";
import { abbreviateName, formatName } from "@hooks/functions/formatName";
import {
  customerStatusSelect,
  CustomerStatusTypes,
  CustomerTypeEnum,
  CustomerTypeTypes,
} from "@hooks/dtos/user-dto";
import { ZuAuthStore } from "@hooks/stores/zustand-auth-store";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import { FormEvent, useCallback, useEffect, useState } from "react";
import {
  FaArrowLeft,
  FaBroom,
  FaEye,
  FaEyeSlash,
  FaKey,
  FaRegCopy,
  FaSort,
  FaSortDown,
  FaSortUp,
  FaUsers,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

type IOrdernation = "ASC" | "DESC" | undefined;

interface IManagementUserDTO {
  id: number;
  type: CustomerTypeTypes;
  name: string;
  email: string;
  document: string;
  documentType: number | null;
  access: object;
  status: CustomerStatusTypes;
  pswdChanged: "t" | "f";
  createdAt: string;
  updatedAt: string;
}

interface IManagementUserListResponseDTO {
  total: number;
  totalPages: number;
  list: IManagementUserDTO[];
}

interface IChangePasswordForm {
  currentPassword: string;
  password: string;
  passwordConfirm: string;
}

type IPasswordVisibility = Record<keyof IChangePasswordForm, boolean>;

const masks = new Masks();
const takeOptions = [10, 30, 50, 100];
const initialChangePasswordForm: IChangePasswordForm = {
  currentPassword: "",
  password: "",
  passwordConfirm: "",
};
const initialPasswordVisibility: IPasswordVisibility = {
  currentPassword: false,
  password: false,
  passwordConfirm: false,
};

const customerTypeSelect: { type: CustomerTypeTypes; text: string }[] = [
  { type: CustomerTypeEnum.CUSTOMER, text: "Cliente" },
  { type: CustomerTypeEnum.SALESPERSON, text: "Consultor" },
  { type: CustomerTypeEnum.MANAGER, text: "Gerente" },
];

const getCustomerTypeText = (type: CustomerTypeTypes) => {
  return (
    customerTypeSelect.find((item) => item.type === type)?.text ||
    "Tipo desconhecido"
  );
};

const formatDocument = (document: string) => {
  const cleanDocument = document.replace(/\D/g, "");
  if (cleanDocument.length === 11) return masks.cpf(cleanDocument);
  if (cleanDocument.length === 14) return masks.cnpj(cleanDocument);
  return document || "~~";
};

export const UserManagementList = () => {
  const navigate = useNavigate();
  const { customer: session } = ZuAuthStore();
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();
  const canEditCustomer =
    session?.customer?.access?.management === true &&
    session?.customer?.access?.customer?.edit === true;

  const [page, setPage] = useState<number>(1);
  const [take, setTake] = useState<number>(10);
  const [userAmount, setUserAmount] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);

  const [search, setSearch] = useState("");
  const [type, setType] = useState<CustomerTypeTypes | undefined>();
  const [status, setStatus] = useState<CustomerStatusTypes | undefined>();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [resultTime, setResultTime] = useState("Todo Período");
  const [orderName, setOrderName] = useState<IOrdernation>();
  const [orderEmail, setOrderEmail] = useState<IOrdernation>();
  const [orderDate, setOrderDate] = useState<IOrdernation>();

  const [newSearch, setNewSearch] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState<IManagementUserDTO[]>([]);
  const [selectedUser, setSelectedUser] = useState<IManagementUserDTO>();
  const [changePasswordForm, setChangePasswordForm] =
    useState<IChangePasswordForm>(initialChangePasswordForm);
  const [passwordVisibility, setPasswordVisibility] =
    useState<IPasswordVisibility>(initialPasswordVisibility);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);

  const buildFilterParams = useCallback(() => {
    return Object.fromEntries(
      Object.entries({
        ...(search && { search }),
        ...(type && { type }),
        ...(status && { status }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(orderName && { orderName }),
        ...(orderEmail && { orderEmail }),
        ...(orderDate && { orderDate }),
      }),
    );
  }, [
    endDate,
    orderDate,
    orderEmail,
    orderName,
    search,
    startDate,
    status,
    type,
  ]);

  const getUserList = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = `?${new URLSearchParams(
        Object.entries({
          page: (page || 1).toString(),
          take: take.toString(),
          ...buildFilterParams(),
        }),
      ).toString()}`;

      const result = await api.get<IManagementUserListResponseDTO>(
        `/customer/management/users/list${params}`,
      );

      setUserAmount(result.data.total);
      setUsers(result.data.list);
      setTotalPages(result.data.totalPages || 1);
      setResultTime(
        startDate.length + endDate.length > 0
          ? "Período Selecionado"
          : "Todo Período",
      );
      setNewSearch(false);
    } catch (error: any) {
      console.error(error);
      pushToastify({
        message:
          error?.message || "Houve um erro ao consultar a relação de usuários!",
        type: "error",
        theme: "dark",
        pauseOnHover: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    buildFilterParams,
    endDate.length,
    page,
    pushToastify,
    startDate.length,
    take,
  ]);

  const cleanFilters = () => {
    setSearch("");
    setType(undefined);
    setStatus(undefined);
    setStartDate("");
    setEndDate("");
    setPage(1);
    setTotalPages(1);
    setNewSearch(true);
  };

  const orderStates = {
    name: { value: orderName, setter: setOrderName },
    email: { value: orderEmail, setter: setOrderEmail },
    date: { value: orderDate, setter: setOrderDate },
  };

  const handleOrder = (orderType: keyof typeof orderStates) => {
    const states: IOrdernation[] = [undefined, "ASC", "DESC"];
    const currentState = orderStates[orderType].value;
    const setState = orderStates[orderType].setter;
    const currentIndex = states.indexOf(currentState);
    const nextIndex = (currentIndex + 1) % states.length;

    setState(states[nextIndex]);
  };

  const getOrdenationIcon = (ordernation: IOrdernation) => {
    switch (ordernation) {
      case "ASC":
        return <FaSortUp className="ms-1" />;
      case "DESC":
        return <FaSortDown className="ms-1" />;
      default:
        return <FaSort className="ms-1" />;
    }
  };

  const handleCopyUserName = async (name: string) => {
    const formattedName = formatName(name);

    try {
      await navigator.clipboard.writeText(formattedName);
      pushToastify({
        message: "Nome do usuÃ¡rio copiado.",
        type: "success",
        theme: "dark",
        pauseOnHover: true,
      });
    } catch (error) {
      pushToastify({
        message: "NÃ£o foi possÃ­vel copiar o nome do usuÃ¡rio.",
        type: "error",
        theme: "dark",
        pauseOnHover: true,
      });
    }
  };

  const openChangePasswordModal = (user: IManagementUserDTO) => {
    if (!canEditCustomer) return;

    setSelectedUser(user);
    setChangePasswordForm(initialChangePasswordForm);
    setPasswordVisibility(initialPasswordVisibility);
    setIsPasswordModalOpen(true);
  };

  const closeChangePasswordModal = () => {
    if (isPasswordSubmitting) return;

    setIsPasswordModalOpen(false);
    setSelectedUser(undefined);
    setChangePasswordForm(initialChangePasswordForm);
    setPasswordVisibility(initialPasswordVisibility);
  };

  const handleChangePasswordForm = (
    field: keyof IChangePasswordForm,
    value: string,
  ) => {
    setChangePasswordForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const togglePasswordVisibility = (field: keyof IChangePasswordForm) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleChangePasswordSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    if (!selectedUser) return;

    if (changePasswordForm.password !== changePasswordForm.passwordConfirm) {
      return pushToastify({
        message: "A nova senha e a confirmaÃ§Ã£o precisam ser iguais.",
        type: "warning",
        theme: "dark",
        pauseOnHover: true,
      });
    }

    setIsPasswordSubmitting(true);
    try {
      await api.post("/customer/management/users/password/update", {
        customerId: selectedUser.id,
        currentPassword: changePasswordForm.currentPassword,
        password: changePasswordForm.password,
        passwordConfirm: changePasswordForm.passwordConfirm,
      });

      pushToastify({
        message: "Senha alterada com sucesso.",
        type: "success",
        theme: "dark",
        pauseOnHover: true,
      });
      setIsPasswordModalOpen(false);
      setSelectedUser(undefined);
      setChangePasswordForm(initialChangePasswordForm);
      setPasswordVisibility(initialPasswordVisibility);
      getUserList();
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Houve um erro ao alterar a senha do usuÃ¡rio.",
        type: "error",
        theme: "dark",
        pauseOnHover: true,
      });
    } finally {
      setIsPasswordSubmitting(false);
    }
  };

  useEffect(() => {
    getUserList();
  }, [getUserList]);

  useEffect(() => {
    if (newSearch) getUserList();
  }, [getUserList, newSearch]);

  return (
    <>
      <div
        className="container-fluid d-flex flex-column justify-content-start align-items-center"
        style={{
          paddingBottom: "2.5rem",
          paddingTop: "clamp(1rem, 2vw, 2rem)",
          paddingLeft: "clamp(1rem, 5vw, 10rem)",
          paddingRight: "clamp(1rem, 5vw, 10rem)",
        }}
      >
        <div className="col-12">
          <Card className="shadow-sm rounded mb-4 border-0">
            <div className="card-body p-4">
              <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3">
                <div className="d-flex align-items-center gap-3">
                  <div className="rounded-3 d-inline-flex align-items-center justify-content-center p-3 bg-primary-subtle text-primary">
                    <FaUsers size={30} />
                  </div>

                  <div>
                    <span className="badge rounded-pill text-bg-primary mb-2">
                      Gerenciamento
                    </span>
                    <h3 className="fw-bold mb-1">Relação de Usuários</h3>
                    <p className="text-muted mb-0">
                      Consulte usuários da tabela de clientes por tipo, status e
                      período de criação.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                  onClick={() => navigate("/management/users")}
                >
                  <FaArrowLeft />
                  Voltar
                </button>
              </div>
            </div>
          </Card>

          <div className="row">
            <div className="col-md-12">
              <Card>
                <div className="card-header">
                  <div className="card-title">Filtrar Registros</div>
                  <div className="card-category">
                    Utilize o filtro para buscar usuários por perfil, status,
                    nome, e-mail ou documento.
                  </div>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-12 col-md-4">
                      <div className="form-group mb-0">
                        <label htmlFor="search">Busca</label>
                        <input
                          id="search"
                          type="text"
                          className="form-control"
                          placeholder="Nome, e-mail, documento..."
                          onChange={(event) => setSearch(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") getUserList();
                          }}
                          value={search}
                        />
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-md-2">
                      <div className="form-group mb-0">
                        <label htmlFor="type">Tipo</label>
                        <select
                          id="type"
                          className="form-control"
                          value={type || ""}
                          onChange={(event) => {
                            const value = event.target.value;
                            setType(
                              value ? (value as CustomerTypeTypes) : undefined,
                            );
                          }}
                        >
                          <option value="">Todos</option>
                          {customerTypeSelect.map((item) => (
                            <option key={item.type} value={item.type}>
                              {item.text}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-md-2">
                      <div className="form-group mb-0">
                        <label htmlFor="status">Status</label>
                        <select
                          id="status"
                          className="form-control"
                          value={status || ""}
                          onChange={(event) => {
                            const value = event.target.value;
                            setStatus(
                              value
                                ? (value as CustomerStatusTypes)
                                : undefined,
                            );
                          }}
                        >
                          <option value="">Todos</option>
                          {customerStatusSelect.map((item) => (
                            <option key={item.status} value={item.status}>
                              {item.text}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-md-2">
                      <div className="form-group mb-0">
                        <label htmlFor="startDate">Data Inicial</label>
                        <input
                          type="date"
                          className="form-control"
                          onChange={(event) => setStartDate(event.target.value)}
                          id="startDate"
                          value={startDate}
                        />
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-md-2">
                      <div className="form-group mb-0">
                        <label htmlFor="endDate">Data Final</label>
                        <input
                          type="date"
                          className="form-control"
                          onChange={(event) => setEndDate(event.target.value)}
                          id="endDate"
                          value={endDate}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row mt-3">
                    <div className="col-12 d-flex justify-content-end gap-2">
                      <button
                        className="btn btn-sm btn-success px-5"
                        onClick={() => getUserList()}
                      >
                        <span className="text-white">Filtrar</span>
                      </button>
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={cleanFilters}
                        title="Limpar todos os filtros."
                      >
                        <FaBroom className="text-white" />
                      </button>
                    </div>
                  </div>
                </div>
                <span className="position-absolute end-0 bg-info text-white p-2 rounded-end rounded-bottom-0">
                  {resultTime}
                </span>
              </Card>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <Card>
                <div className="card-header">
                  <div className="row">
                    <div className="col-md-7">
                      <h4 className="card-title">Relação de Usuários</h4>
                      <p className="card-category">
                        Lista de usuários cadastrados na tabela de clientes.
                      </p>
                    </div>
                    <div className="col-md-5 d-flex flex-column flex-md-row justify-content-md-end align-items-md-center gap-2 mt-3 mt-md-0">
                      <div
                        className="input-group input-group-sm"
                        style={{ maxWidth: 210 }}
                      >
                        <label className="input-group-text" htmlFor="take">
                          Exibir
                        </label>
                        <select
                          id="take"
                          className="form-select"
                          value={take}
                          onChange={(event) => {
                            setPage(1);
                            setTake(Number(event.target.value));
                          }}
                        >
                          {takeOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                        <span className="input-group-text">por página</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  <div className="table-responsive">
                    <div className="dataTables_wrapper container-fluid dt-bootstrap4">
                      <div className="dataTables_length">
                        <table className="table table-striped">
                          <thead>
                            <tr role="row">
                              <th>ID</th>
                              <th
                                style={{ cursor: "pointer" }}
                                onClick={() => handleOrder("name")}
                              >
                                Usuário {getOrdenationIcon(orderName)}
                              </th>
                              <th
                                style={{ cursor: "pointer" }}
                                onClick={() => handleOrder("email")}
                              >
                                E-mail {getOrdenationIcon(orderEmail)}
                              </th>
                              <th>Tipo</th>
                              <th>Documento</th>
                              <th>Status</th>
                              <th>Senha</th>
                              <th
                                className="d-flex align-items-center"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleOrder("date")}
                              >
                                Criação {getOrdenationIcon(orderDate)}
                              </th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {isLoading ? (
                              <tr>
                                <td colSpan={9} className="text-center">
                                  <LoadingSkeleton />
                                </td>
                              </tr>
                            ) : (
                              <>
                                {users.length > 0 &&
                                  users.map((user) => {
                                    const convertedStatus =
                                      CustomerConvertStatus(user.status);

                                    return (
                                      <tr key={user.id}>
                                        <td>{user.id}</td>
                                        <td title={formatName(user.name)}>
                                          <div className="d-flex align-items-center gap-1">
                                            <button
                                              type="button"
                                              className="btn btn-link btn-sm text-muted p-0"
                                              title={`Copiar Nome: ${formatName(user.name)}`}
                                              onClick={() =>
                                                handleCopyUserName(user.name)
                                              }
                                            >
                                              <FaRegCopy />
                                            </button>
                                            <span>
                                              {formatName(
                                                abbreviateName(user.name, 26),
                                              )}
                                            </span>
                                          </div>
                                        </td>
                                        <td>{user.email}</td>
                                        <td>
                                          {getCustomerTypeText(user.type)}
                                        </td>
                                        <td>{formatDocument(user.document)}</td>
                                        <td title={convertedStatus.description}>
                                          <span
                                            className={`fs-6 badge badge-${convertedStatus.color}`}
                                          >
                                            {convertedStatus.text}
                                          </span>
                                        </td>
                                        <td>
                                          <span
                                            className={`fs-6 badge badge-${
                                              user.pswdChanged === "t"
                                                ? "success"
                                                : "warning"
                                            }`}
                                          >
                                            {user.pswdChanged === "t"
                                              ? "Alterada"
                                              : "Pendente"}
                                          </span>
                                        </td>
                                        <td>
                                          {dayjs(user.createdAt).format(
                                            "DD/MM/YYYY",
                                          )}
                                        </td>
                                        <td className="text-center">
                                          <button
                                            type="button"
                                            disabled
                                            title="Ver Dados"
                                            className="badge badge-info me-2 border-0"
                                          >
                                            <FaEye className="fs-5" />
                                          </button>
                                          <button
                                            type="button"
                                            disabled={!canEditCustomer}
                                            title="Alterar Senha"
                                            className="badge badge-warning border-0"
                                            onClick={() =>
                                              openChangePasswordModal(user)
                                            }
                                          >
                                            <FaKey className="fs-5 text-white" />
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}

                                {users.length === 0 && (
                                  <tr>
                                    <td colSpan={9} className="text-center">
                                      Nenhum registro encontrado.
                                    </td>
                                  </tr>
                                )}
                              </>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-center">
                    <span>
                      Quantidade de Registros: {masks.formatMilhar(userAmount)}
                    </span>
                    <Pagination
                      page={page}
                      setPage={setPage}
                      totalPages={totalPages}
                      maxPageShow={10}
                    />
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {isPasswordModalOpen && selectedUser && (
        <>
          <div
            className="modal fade show"
            style={{ display: "block" }}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="changePasswordModalLabel"
          >
            <div className="modal-dialog modal-dialog-centered">
              <form
                className="modal-content"
                onSubmit={handleChangePasswordSubmit}
              >
                <div className="modal-header">
                  <h5
                    className="modal-title fw-bold"
                    id="changePasswordModalLabel"
                  >
                    Alterar senha
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Fechar"
                    onClick={closeChangePasswordModal}
                    disabled={isPasswordSubmitting}
                  />
                </div>

                <div className="modal-body">
                  <div className="alert alert-warning mb-3">
                    Alterando a senha de{" "}
                    <strong>{formatName(selectedUser.name)}</strong>.
                  </div>

                  <div className="form-group">
                    <label htmlFor="currentPassword">
                      Sua senha de Suporte
                    </label>
                    <div className="input-group">
                      <input
                        id="currentPassword"
                        type={
                          passwordVisibility.currentPassword
                            ? "text"
                            : "password"
                        }
                        className="form-control"
                        placeholder="Senha da sua conta logada"
                        value={changePasswordForm.currentPassword}
                        onChange={(event) =>
                          handleChangePasswordForm(
                            "currentPassword",
                            event.target.value,
                          )
                        }
                        required
                        autoFocus
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() =>
                          togglePasswordVisibility("currentPassword")
                        }
                        title={
                          passwordVisibility.currentPassword
                            ? "Ocultar senha"
                            : "Visualizar senha"
                        }
                      >
                        {passwordVisibility.currentPassword ? (
                          <FaEyeSlash />
                        ) : (
                          <FaEye />
                        )}
                      </button>
                    </div>
                    <small className="text-muted">
                      Essa senha confirma a sua permissão como Suporte.
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="password">Nova senha</label>
                    <div className="input-group">
                      <input
                        id="password"
                        type={passwordVisibility.password ? "text" : "password"}
                        className="form-control"
                        value={changePasswordForm.password}
                        onChange={(event) =>
                          handleChangePasswordForm(
                            "password",
                            event.target.value,
                          )
                        }
                        required
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => togglePasswordVisibility("password")}
                        title={
                          passwordVisibility.password
                            ? "Ocultar senha"
                            : "Visualizar senha"
                        }
                      >
                        {passwordVisibility.password ? (
                          <FaEyeSlash />
                        ) : (
                          <FaEye />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="form-group mb-0">
                    <label htmlFor="passwordConfirm">
                      Confirmar nova senha
                    </label>
                    <div className="input-group">
                      <input
                        id="passwordConfirm"
                        type={
                          passwordVisibility.passwordConfirm
                            ? "text"
                            : "password"
                        }
                        className="form-control"
                        value={changePasswordForm.passwordConfirm}
                        onChange={(event) =>
                          handleChangePasswordForm(
                            "passwordConfirm",
                            event.target.value,
                          )
                        }
                        required
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() =>
                          togglePasswordVisibility("passwordConfirm")
                        }
                        title={
                          passwordVisibility.passwordConfirm
                            ? "Ocultar senha"
                            : "Visualizar senha"
                        }
                      >
                        {passwordVisibility.passwordConfirm ? (
                          <FaEyeSlash />
                        ) : (
                          <FaEye />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={closeChangePasswordModal}
                    disabled={isPasswordSubmitting}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-warning text-white d-flex align-items-center gap-2"
                    disabled={isPasswordSubmitting}
                  >
                    <FaKey />
                    {isPasswordSubmitting ? "Alterando..." : "Alterar senha"}
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </>
  );
};
